import classNames from "classnames";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import styles from "./accordion.module.scss";

interface HeaderInterface {
  isOpen: boolean;
  onToggleAccordion: () => void;
}

interface AccordionInterface {
  className?: string;
  header: ({ isOpen, onToggleAccordion }: HeaderInterface) => JSX.Element;
  children: JSX.Element;
  overflowedChildren?: JSX.Element;
  isOpenDefault?: boolean;
  isScroll?: boolean;
  accordionScrollWrapperId?: string;

  /** Animation speed: pixels/second */
  speedPxPerSec?: number;
  /** Min/max duration (ms) */
  minDurationMs?: number;
  maxDurationMs?: number;
}

const EVT_DELTA = "accordion:delta";

type DeltaDetail = { delta: number };

declare global {
  interface HTMLElementEventMap {
    "accordion:delta": CustomEvent<DeltaDetail>;
  }
}

const Accordion: React.FC<AccordionInterface> = ({
  className,
  header,
  children,
  overflowedChildren,
  isOpenDefault = false,
  isScroll = false,
  accordionScrollWrapperId,
  speedPxPerSec = 1400,
  minDurationMs = 120,
  maxDurationMs = 360
}) => {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState<boolean>(isOpenDefault);

  const animRef = useRef<Animation | null>(null);
  const heightRef = useRef<number>(0);
  const contentHRef = useRef<number>(0); // current content height

  const roRafId = useRef<number | null>(null);
  const isAnimatingRef = useRef(false);

  // Target/state control (anti-rollback)
  const targetRef = useRef<number>(0);
  const isOpenRef = useRef<boolean>(isOpenDefault);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const onToggleAccordion = useCallback(() => setIsOpen((p) => !p), []);

  const readCurrentHeight = useCallback(
    (el: HTMLElement): number =>
      parseFloat(getComputedStyle(el).height || "0") || 0,
    []
  );

  const getPageViewHeight = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return Infinity;
    const bottomPadding = 20;
    const top = body.getBoundingClientRect().top;
    return Math.max(0, window.innerHeight - top - bottomPadding);
  }, []);

  const viewportClamp = useCallback(
    (wanted: number) => {
      if (!isScroll) return Math.max(0, wanted);
      const pvH = getPageViewHeight();
      return Math.max(0, Math.min(wanted, pvH));
    },
    [isScroll, getPageViewHeight]
  );

  const calcTargetHeight = useCallback((): number => {
    const content = contentRef.current;
    if (!content) return 0;
    const contentHeight = content.scrollHeight;
    return viewportClamp(contentHeight);
  }, [viewportClamp]); // viewportClamp uses isScroll indirectly

  const pickDuration = useCallback(
    (prevH: number, newH: number) => {
      const dist = Math.abs(newH - prevH);
      const raw = (dist / Math.max(1, speedPxPerSec)) * 1000;
      return Math.min(maxDurationMs, Math.max(minDurationMs, Math.round(raw)));
    },
    [speedPxPerSec, minDurationMs, maxDurationMs]
  );

  // Synchronous message to ancestors about altitude delta
  const dispatchDeltaUp = useCallback((delta: number) => {
    const body = bodyRef.current;
    if (!body || delta === 0) return;
    const evt = new CustomEvent(EVT_DELTA, {
      bubbles: true,
      composed: true,
      detail: { delta }
    });
    body.dispatchEvent(evt);
  }, []);

  const animateTo = useCallback(
    (
      newH: number,
      { announceDelta = false }: { announceDelta?: boolean } = {}
    ) => {
      const body = bodyRef.current;
      if (!body) return;

      const prevH = readCurrentHeight(body);
      const clamped = viewportClamp(newH);

      targetRef.current = clamped;

      if (Math.abs(clamped - prevH) < 0.5) {
        body.style.height = `${clamped}px`;
        heightRef.current = clamped;
        return;
      }

      if (announceDelta) {
        dispatchDeltaUp(clamped - prevH);
      }

      animRef.current?.cancel();

      const duration = pickDuration(prevH, clamped);
      isAnimatingRef.current = true;

      const anim = body.animate(
        [{ height: `${prevH}px` }, { height: `${clamped}px` }],
        {
          duration,
          easing:
            clamped > prevH
              ? "cubic-bezier(.25,.8,.25,1)"
              : "cubic-bezier(.22,.61,.36,1)",
          fill: "forwards"
        }
      );

      animRef.current = anim;

      anim.onfinish = () => {
        if (targetRef.current !== clamped) return;

        body.style.height = `${clamped}px`;
        heightRef.current = clamped;
        animRef.current = null;
        isAnimatingRef.current = false;

        // Reach only when the ultimate goal is open
        if (isOpenRef.current) {
          const maybeAdjust = calcTargetHeight();
          if (Math.abs(maybeAdjust - clamped) > 0.5) {
            const quick = Math.min(120, pickDuration(clamped, maybeAdjust));
            const a2 = body.animate(
              [{ height: `${clamped}px` }, { height: `${maybeAdjust}px` }],
              { duration: quick, easing: "linear", fill: "forwards" }
            );
            a2.onfinish = () => {
              if (targetRef.current === maybeAdjust) {
                body.style.height = `${maybeAdjust}px`;
                heightRef.current = maybeAdjust;
              }
            };
          }
        }
      };

      anim.oncancel = () => {
        heightRef.current = readCurrentHeight(body);
        animRef.current = null;
        isAnimatingRef.current = false;
      };
    },
    [
      calcTargetHeight,
      pickDuration,
      readCurrentHeight,
      viewportClamp,
      dispatchDeltaUp
    ]
  );

  // Initial state
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    body.style.willChange = "height";
    body.style.overflowY = isScroll ? "auto" : "hidden";

    if (isOpenDefault) {
      const h = calcTargetHeight();
      body.style.height = `${h}px`;
      heightRef.current = h;
      targetRef.current = h;
    } else {
      body.style.height = "0px";
      heightRef.current = 0;
      targetRef.current = 0;
    }
  }, [isOpenDefault, isScroll, calcTargetHeight]);

  // User click — simultaneously animate ancestors (announceDelta)
  useEffect(() => {
    const target = isOpen ? calcTargetHeight() : 0;
    animateTo(target, { announceDelta: true });
  }, [isOpen, calcTargetHeight, animateTo]);

  // Content changes (incl. nested) — RO
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const ro = new ResizeObserver(() => {
      // updating the content height cache
      contentHRef.current = content.scrollHeight;

      if (!isOpen) return;
      if (isAnimatingRef.current) return;
      if (targetRef.current === 0) return;

      if (roRafId.current) cancelAnimationFrame(roRafId.current);
      roRafId.current = requestAnimationFrame(() => {
        const target = calcTargetHeight();
        if (Math.abs(target - (heightRef.current ?? 0)) > 0.5) {
          animateTo(target);
        }
        roRafId.current = null;
      });
    });

    ro.observe(content);
    return () => {
      ro.disconnect();
      if (roRafId.current) cancelAnimationFrame(roRafId.current);
    };
  }, [isOpen, calcTargetHeight, animateTo]);

  // Resize
  useEffect(() => {
    const onResize = () => {
      if (!isOpen) return;
      if (isAnimatingRef.current) return;
      if (targetRef.current === 0) return;
      animateTo(calcTargetHeight());
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isOpen, calcTargetHeight, animateTo]);

  // Delta listener from subsidiaries — fix "elasticity" on clamp
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const EPS = 0.5;

    const onDelta = (e: CustomEvent<DeltaDetail>) => {
      if (e.target === body) return; // only from children
      const { delta } = e.detail || {};
      if (typeof delta !== "number" || delta === 0) return;

      if (!isOpenRef.current || targetRef.current === 0) return;

      const curH = readCurrentHeight(body);
      const pvH = isScroll ? getPageViewHeight() : Infinity;

      // prediction of new content height after changing child
      const predictedContent = Math.max(0, (contentHRef.current || 0) + delta);
      const desired = isScroll
        ? Math.min(predictedContent, pvH)
        : predictedContent;

      const atClampNow = isScroll && Math.abs(curH - pvH) < EPS;
      const atClampAfter = isScroll && Math.abs(desired - pvH) < EPS;

      // If both clamp now and clamp after delta — do not animate (no visible effect)
      if (atClampNow && atClampAfter) return;

      if (Math.abs(desired - curH) < EPS) return;

      animateTo(desired);
    };

    body.addEventListener(EVT_DELTA, onDelta);
    return () => body.removeEventListener(EVT_DELTA, onDelta);
  }, [animateTo, isScroll, getPageViewHeight, readCurrentHeight]);

  return (
    <div className={classNames(styles["wrapper"], className)}>
      <div className={styles["wrapper__header"]}>
        {header({ isOpen, onToggleAccordion })}
      </div>

      {overflowedChildren && isOpen && (
        <div style={{ position: "absolute", top: 0 }}>{overflowedChildren}</div>
      )}

      <div
        id={accordionScrollWrapperId}
        className={classNames(styles["wrapper__body"], {
          [styles["wrapper__body--scroll"]]: isScroll
        })}
        ref={bodyRef}
      >
        <div className={styles["wrapper__content"]} ref={contentRef}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Accordion;
